/**
 * Review Moderation Hook
 *
 * Fetches pending/flagged reviews and provides approve/reject/flag actions.
 *
 * API Endpoints:
 *   GET  /api/v1/reviews/pending?limit=20&offset=0
 *   POST /api/v1/reviews/:reviewId/approve
 *   POST /api/v1/reviews/:reviewId/reject   { reason }
 *   POST /api/v1/reviews/:reviewId/flag      { reason }
 *
 * Story: SCRUM-840
 */

import { useState, useEffect, useCallback } from 'react';
import apiRequest from '../../services/api';

export default function useReviewModeration({ limit = 20, offset = 0 } = {}) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('offset', String(offset));

      const response = await apiRequest(`/api/v1/reviews/pending?${params.toString()}`);

      if (response.success) {
        setReviews(response.data || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [limit, offset]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const approveReview = useCallback(async (reviewId) => {
    setActionLoading(true);
    try {
      const response = await apiRequest(`/api/v1/reviews/${reviewId}/approve`, {
        method: 'POST',
      });

      if (response.success) {
        setReviews(prev => prev.filter(r => r.reviewId !== reviewId));
      }
      return response;
    } finally {
      setActionLoading(false);
    }
  }, []);

  const rejectReview = useCallback(async (reviewId, reason) => {
    setActionLoading(true);
    try {
      const response = await apiRequest(`/api/v1/reviews/${reviewId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.success) {
        setReviews(prev => prev.filter(r => r.reviewId !== reviewId));
      }
      return response;
    } finally {
      setActionLoading(false);
    }
  }, []);

  const flagReview = useCallback(async (reviewId, reason) => {
    setActionLoading(true);
    try {
      const response = await apiRequest(`/api/v1/reviews/${reviewId}/flag`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.success) {
        setReviews(prev =>
          prev.map(r =>
            r.reviewId === reviewId
              ? { ...r, moderationStatus: 'flagged' }
              : r
          )
        );
      }
      return response;
    } finally {
      setActionLoading(false);
    }
  }, []);

  return {
    reviews,
    loading,
    error,
    actionLoading,
    refresh: fetchReviews,
    approveReview,
    rejectReview,
    flagReview,
  };
}
